import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CurrencyExchange as CurrencyIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import CurrencyManagement from './CurrencyManagement';

const drawerWidth = 240;

const Dashboard = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('currency');
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
  };

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
    { id: 'currency', label: 'Currency', icon: <CurrencyIcon /> },
    { id: 'users', label: 'Users', icon: <PeopleIcon /> },
    { id: 'reports', label: 'Reports', icon: <AssessmentIcon /> },
    { id: 'accounts', label: 'Accounts', icon: <AccountBalanceIcon /> },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon /> }
  ];
  

  const renderContent = () => {
    switch (selectedMenu) {
      case 'currency':
        return <CurrencyManagement />;
      case 'dashboard':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ 
              background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold'
            }}>
              Dashboard
            </Typography>
            <Typography variant="body1" sx={{ color: '#666666', mb: 3 }}>
              Welcome to the Circle Exchange Admin Portal
            </Typography>
            <Box sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, rgba(184, 134, 11, 0.05) 0%, rgba(218, 165, 32, 0.02) 100%)',
              borderRadius: 2,
              border: '1px solid rgba(184, 134, 11, 0.2)'
            }}>
              <Typography variant="h6" sx={{ color: '#2C2C2C', fontWeight: 600 }}>
                System Overview
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666' }}>
                Manage your trading system efficiently with our comprehensive admin tools.
              </Typography>
            </Box>
          </Box>
        );
      case 'users':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              User Management
            </Typography>
            <Typography variant="body1">
              Manage system users here
            </Typography>
          </Box>
        );
      case 'reports':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Reports
            </Typography>
            <Typography variant="body1">
              View trading reports and analytics
            </Typography>
          </Box>
        );
      case 'accounts':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Account Management
            </Typography>
            <Typography variant="body1">
              Manage trading accounts
            </Typography>
          </Box>
        );
      case 'settings':
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Settings
            </Typography>
            <Typography variant="body1">
              System configuration and settings
            </Typography>
          </Box>
        );
      default:
        return <CurrencyManagement />;
    }
  };

  const drawer = (
    <div>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 2 }}>
          <img src={require("../assets/picture/Circle_exchange_logo.jpg")} alt="Logo" style={{ width: 150 }} />
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: 'rgba(184, 134, 11, 0.2)' }} />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={selectedMenu === item.id}
              onClick={() => setSelectedMenu(item.id)}
              sx={{
                margin: '2px 8px',
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(184, 134, 11, 0.1)',
                  borderLeft: '4px solid #B8860B',
                  '&:hover': {
                    backgroundColor: 'rgba(184, 134, 11, 0.15)',
                  }
                },
                '&:hover': {
                  backgroundColor: 'rgba(184, 134, 11, 0.05)',
                }
              }}
            >
              <ListItemIcon sx={{ 
                color: selectedMenu === item.id ? '#B8860B' : '#666666',
                minWidth: 40
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label} 
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    color: selectedMenu === item.id ? '#B8860B' : '#2C2C2C',
                    fontWeight: selectedMenu === item.id ? 600 : 400,
                    fontSize: '0.95rem'
                  } 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(184, 134, 11, 0.2)', mt: 2 }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              margin: '2px 8px',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.08)',
              }
            }}
          >
            <ListItemIcon sx={{ color: '#666666', minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  color: '#2C2C2C',
                  fontSize: '0.95rem'
                } 
              }} 
            />
          </ListItemButton>
        </ListItem>
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {renderContent()}
      </Box>
    </Box>
  );
};

export default Dashboard;
